import type { AccidentPhotoCategory, AccidentReportRow, CompanySettings } from '@routeback/core';

/**
 * Accident report — the Driver Portal's form (FedEx accident packet, OP-135),
 * kept as one JSON object on the report. Every field is optional except the
 * ones the plugin required: date, truck, street, city and what happened (the
 * database checks the same list on submit).
 */
export interface OtherVehicle {
  owner: string; address: string; phone: string; vehicle: string; plate: string; plate_state: string;
  insurance: string; policy: string;
  driver_name: string; driver_dob: string; driver_phone: string; driver_address: string; driver_license: string; driver_license_state: string;
}
export interface Witness { name: string; address: string; phone: string; age: string }
export interface AccidentData {
  date: string; time: string; fedex_id: string; unit_other: string;
  vehicle_type: string; ownership: string; vedr: string; vedr_possession: string;
  loc: { street: string; block: string; city: string; county: string; state: string; landmark: string; property_type: string };
  others: OtherVehicle[];
  witnesses: Witness[];
  police: { called: string; jurisdiction: string; officer: string; badge: string; post: string; cited: string; violation: string };
  road: { speed_limit: string; lanes: string; characteristics: string[]; surface: string[]; weather: string[]; visibility: string };
  property: { damage: string; owner: string; phone: string };
  description: string; injuries: string; notes: string;
}

export const blankVehicle = (): OtherVehicle => ({
  owner: '', address: '', phone: '', vehicle: '', plate: '', plate_state: '', insurance: '', policy: '',
  driver_name: '', driver_dob: '', driver_phone: '', driver_address: '', driver_license: '', driver_license_state: '',
});
export const blankWitness = (): Witness => ({ name: '', address: '', phone: '', age: '' });

export function emptyData(today: string, now: string): AccidentData {
  return {
    date: today, time: now, fedex_id: '', unit_other: '', vehicle_type: '', ownership: '', vedr: '', vedr_possession: '',
    loc: { street: '', block: '', city: '', county: '', state: '', landmark: '', property_type: '' },
    others: [], witnesses: [blankWitness()],
    police: { called: '', jurisdiction: '', officer: '', badge: '', post: '', cited: '', violation: '' },
    road: { speed_limit: '', lanes: '', characteristics: [], surface: [], weather: [], visibility: '' },
    property: { damage: '', owner: '', phone: '' },
    description: '', injuries: '', notes: '',
  };
}

/** Fill any missing keys (older drafts, partial data) so screens never hit undefined. */
export function withDefaults(raw: Record<string, unknown> | null | undefined, today: string, now: string): AccidentData {
  const e = emptyData(today, now);
  const d = (raw ?? {}) as Partial<AccidentData>;
  return {
    ...e, ...d,
    loc: { ...e.loc, ...(d.loc ?? {}) },
    police: { ...e.police, ...(d.police ?? {}) },
    road: { ...e.road, ...(d.road ?? {}) },
    property: { ...e.property, ...(d.property ?? {}) },
    others: (d.others ?? []).map((v) => ({ ...blankVehicle(), ...v })),
    witnesses: (d.witnesses?.length ? d.witnesses : e.witnesses).map((w) => ({ ...blankWitness(), ...w })),
  };
}

// The plugin's choices, word for word.
export const VEHICLE_TYPES = ['Straight Truck', 'Step Van', 'Box Truck', 'Cargo Van', 'Tractor'];
export const OWNERSHIP = ['SP Owned/Leased', 'Rental'];
export const PROPERTY_TYPES = ['Residential', 'Rural', 'Business', 'Private Property'];
export const ROAD_CHARACTERISTICS = ['Straight', 'Curve', 'Level', 'Grade', 'Hillcrest', 'Marked', 'Divided', '2-Way', 'One-Way'];
export const SURFACES = ['Dry', 'Wet', 'Mud', 'Ice', 'Snow', 'Oil'];
export const WEATHER = ['Clear', 'Rain', 'Fog', 'Sleet', 'Snow'];
export const VISIBILITY = ['Dawn', 'Day', 'Dusk', 'Dark', 'Artificial light'];

export const STEPS = [
  'Driver & vehicle', 'Accident location', 'Other vehicle(s)', 'Witnesses & police', 'Road & weather',
  'What happened?', 'Photos — your vehicle', 'Photos — other vehicle', 'Photos — scene', 'Review & submit',
] as const;

/** The plugin's photo prompts: [category, title, tip]. */
export const PHOTO_PROMPTS: Record<number, [AccidentPhotoCategory, string, string][]> = {
  6: [
    ['my_vehicle', 'Wide view of vehicle', 'Stand back far enough to get the whole truck and where it sits.'],
    ['my_vehicle', 'Close-ups of damage', 'Every damaged area, close enough to see it clearly.'],
  ],
  7: [
    ['other_vehicle', 'Wide view of other vehicle', 'The whole vehicle and where it sits.'],
    ['other_vehicle', 'Close-ups of damage & license plate', 'Damage, the license plate, and the VIN if you can.'],
  ],
  8: [
    ['scene', 'Wide scene overview', 'Road, position of vehicles, skid marks, traffic signs or signals.'],
    ['scene', 'Road signs & signals', 'Stop signs, traffic lights, speed limit signs, lane markings near the accident.'],
    ['other', 'Other / additional', 'Insurance cards, driver licenses, anything not covered above.'],
  ],
};
export const PHOTOS_PER_PROMPT = 8;
export const MAX_PHOTOS = 40;

/** What's still needed before it can be sent (same list the database checks). */
export function missingFields(r: Pick<AccidentReportRow, 'truck_id'>, d: AccidentData): { label: string; step: number }[] {
  const out: { label: string; step: number }[] = [];
  if (!d.date) out.push({ label: 'Date', step: 0 });
  if (!r.truck_id && !d.unit_other.trim()) out.push({ label: 'Unit #', step: 0 });
  if (!d.loc.street.trim()) out.push({ label: 'Street / highway', step: 1 });
  if (!d.loc.city.trim()) out.push({ label: 'City', step: 1 });
  if (!d.description.trim()) out.push({ label: 'What happened', step: 5 });
  return out;
}

/** occurred_at from the date + time the driver typed (their phone's clock and zone). */
export function occurredAt(d: Pick<AccidentData, 'date' | 'time'>): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.date)) return null;
  const t = /^\d{2}:\d{2}/.test(d.time) ? d.time.slice(0, 5) : '12:00';
  const dt = new Date(`${d.date}T${t}:00`);
  return Number.isNaN(dt.getTime()) ? null : dt.toISOString();
}

/** "I-294 North, Oak Brook, IL" — the short location shown in lists. */
export function locationLine(d: AccidentData): string {
  return [d.loc.street, d.loc.city, d.loc.state].map((x) => x.trim()).filter(Boolean).join(', ');
}

/** Emergency numbers on the safety screen (Company settings → Accident report). */
export interface EmergencyContact { label: string; phone: string }
export function emergencyContacts(settings: CompanySettings): EmergencyContact[] {
  const v = settings['accident_contacts'];
  return Array.isArray(v) ? v.filter((c): c is EmergencyContact => !!c && typeof c.label === 'string' && typeof c.phone === 'string' && !!c.phone.trim()) : [];
}
export const telHref = (phone: string): string => `tel:${phone.replace(/[^\d+]/g, '')}`;

/** Toggle a value in a multi-select chip list. */
export const toggleIn = (list: string[], v: string): string[] => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

export interface SummarySection { title: string; rows: [string, string][]; text?: string }
const keep = (rows: [string, string | undefined | false][]): [string, string][] => rows.filter((r): r is [string, string] => !!r[1] && !!String(r[1]).trim());
/**
 * The report as titled label/value sections — the same content as the
 * on-screen summary, for the PDF (and later the email).
 */
export function summarySections(d: AccidentData, driver: string, unit: string, when: string): SummarySection[] {
  return [
    { title: 'Driver & vehicle', rows: keep([['Driver', driver], ['FedEx ID', d.fedex_id], ['When', when], ['Unit #', unit || d.unit_other], ['Vehicle type', d.vehicle_type],
      ['Ownership', d.ownership], ['VEDR camera in vehicle', d.vedr], ['VEDR camera is with', d.vedr === 'No' && d.vedr_possession]]) },
    { title: 'Location', rows: keep([['Street / highway', d.loc.street], ['Block / mile marker', d.loc.block], ['City', d.loc.city], ['County', d.loc.county], ['State', d.loc.state],
      ['Nearest landmark', d.loc.landmark], ['Property type', d.loc.property_type]]) },
    ...d.others.map((v, i) => ({ title: `Other vehicle ${i + 1}`, rows: keep([['Vehicle', v.vehicle], ['Plate', [v.plate, v.plate_state].filter(Boolean).join(' ')],
      ['Insurance', [v.insurance, v.policy && `policy ${v.policy}`].filter(Boolean).join(', ')], ['Owner', [v.owner, v.address, v.phone].filter(Boolean).join(' · ')],
      ['Driver', [v.driver_name, v.driver_phone, v.driver_address].filter(Boolean).join(' · ')], ['Driver DOB', v.driver_dob],
      ['Driver’s license', [v.driver_license, v.driver_license_state].filter(Boolean).join(' ')]]) })),
    { title: 'Witnesses & police', rows: keep([...d.witnesses.map((w, i) => [`Witness ${i + 1}`, [w.name, w.phone, w.address, w.age && `about ${w.age}`].filter(Boolean).join(' · ')] as [string, string]),
      ['Police called', d.police.called], ['Jurisdiction', d.police.jurisdiction], ['Officer', [d.police.officer, d.police.badge && `badge ${d.police.badge}`].filter(Boolean).join(', ')],
      ['Post / district', d.police.post], ['Cited', d.police.cited], ['Violation', d.police.violation]]) },
    { title: 'Road & weather', rows: keep([['Speed limit', d.road.speed_limit && `${d.road.speed_limit} mph`], ['Lanes', d.road.lanes], ['Road', d.road.characteristics.join(', ')],
      ['Surface', d.road.surface.join(', ')], ['Weather', d.road.weather.join(', ')], ['Visibility', d.road.visibility],
      ['Property damaged', d.property.damage], ['Property owner', [d.property.owner, d.property.phone].filter(Boolean).join(' · ')]]) },
    { title: 'What happened', rows: keep([['Injuries', d.injuries], ['Other notes', d.notes]]), text: d.description },
  ];
}
